import fileSize from 'filesize';
import { padZeros } from '../numbers/numberHelper';

export type UploadedFileEntityType = 'document' | 'image' | 'video';

export const MAX_UPLOAD_SIZE_IN_BYTES = 104857600;

export const acceptedImageFileExtensions = [
    '.tiff',
    '.pjp',
    '.jfif',
    '.bmp',
    '.svg',
    '.png',
    '.xbm',
    '.dib',
    '.jxl',
    '.jpeg',
    '.jpg',
    '.webp',
    '.ico',
    '.tif',
    '.pjpeg',
];

export const acceptedDocumentFileExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx'];

export const acceptedVideoFileExtensions = ['.m4v', '.mp4', '.3gp', '.mov'];

export const acceptedPhotoAndVideoFileExtensions = acceptedImageFileExtensions.concat(acceptedVideoFileExtensions);

export const isDocument = (file: File) => {
    return isDocumentFilename(file.name);
};

export const isDocumentFilename = (filename: string) => {
    return acceptedDocumentFileExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
};

export const isImage = (file: File) => {
    return isImageFilename(file.name);
};

export const isImageFilename = (filename: string) => {
    return acceptedImageFileExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
};

export const isVideo = (file: File) => {
    return isVideoFilename(file.name);
};

export const isVideoFilename = (filename: string) => {
    return acceptedVideoFileExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
};

export const getVideoDurationString = (duration: number) => {
    const minute = 60;
    const hour = minute * 60;

    if (duration < 0) {
        return 'invalid duration';
    } else if (duration <= 1) {
        return `1 second`;
    } else if (duration < minute) {
        return `${Math.round(duration)} seconds`;
    } else if (duration <= minute * 1.5) {
        return '1 min';
    } else if (duration < hour) {
        const minutes = duration / minute;
        return `${Math.round(minutes)} mins`;
    } else if (duration >= hour) {
        const hours = Math.floor(duration / (60 * 60));
        const minutes = Math.floor(duration / 60) - hours * 60;
        const seconds = duration % 60;
        return `${padZeros(hours, 2)}:${padZeros(minutes, 2)}:${padZeros(seconds, 2)}`;
    } else {
        return 'Calculating duration...';
    }
};

export const getFileEntityType = (file: File): UploadedFileEntityType => {
    if (isImage(file)) return 'image';
    if (isVideo(file)) return 'video';
    return 'document';
};

export const getFilenameEntityType = (filename: string): UploadedFileEntityType => {
    if (isImageFilename(filename)) return 'image';
    if (isVideoFilename(filename)) return 'video';
    return 'document';
};

export const formatFileSizeFromBytes = (bytes: number, decimalPlaces = 1, base = 10) => {
    return fileSize(bytes, {
        base: base,
        round: decimalPlaces,
    });
};

export const getOriginalFileName = (awsFilename: string) => {
    let fileName = awsFilename;

    if (fileName.includes('-')) {
        const extension = awsFilename.split('.').pop();
        const dashParts = awsFilename.split('-');

        // remove everything after the last dash
        dashParts.pop();

        fileName = `${dashParts.join('-')}.${extension}`;
    }
    return fileName;
};

export const getFileExtension = (fileName: string) => {
    if (!fileName || !fileName.includes('.')) {
        return;
    }

    const fileParts = fileName.split('.');
    return fileParts[fileParts.length - 1];
};

export const truncateLongFileName = (fileName: string, maxLength?: number) => {
    if (maxLength && fileName.length <= maxLength) {
        return fileName;
    }

    if (maxLength && maxLength < 10) {
        return fileName;
    }

    const chunk = maxLength ? Math.floor(maxLength / 3) : 10;

    const fileNameParts = fileName.split('.');
    const extension = fileNameParts.pop();
    const fileNameNoExtension = fileNameParts.join('.');

    const firstChunk = fileNameNoExtension.substring(0, chunk);
    const lastChunk = fileNameNoExtension.substring(fileNameNoExtension.length - chunk, fileNameNoExtension.length);
    return `${firstChunk}...${lastChunk}.${extension}`;
};
